import { useState } from 'react';
import { FotoboxRecord } from '../types';

interface HeaderProps {
  boxes: FotoboxRecord[];
  lastUpdated: Date;
  isAdmin: boolean;
  onSettingsOpen: () => void;
  onSubscriptionsOpen: () => void;
  onUsageOpen: () => void;
  onLogout: () => void;
}

function StatPill({
  value,
  label,
  variant,
}: {
  value: number;
  label: string;
  variant: 'default' | 'online' | 'offline' | 'warning';
}) {
  const styles: Record<string, string> = {
    default: 'bg-surface-subtle border-surface-border text-ink-secondary',
    online: 'bg-brand-green-light border-brand-green-mid text-brand-green',
    offline: 'bg-brand-red-light border-brand-red-mid text-brand-red',
    warning: 'bg-amber-50 border-amber-200 text-amber-600',
  };
  const dotStyles: Record<string, string> = {
    default: 'bg-ink-faint',
    online: 'bg-brand-green',
    offline: 'bg-brand-red',
    warning: 'bg-amber-400',
  };

  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium ${styles[variant]}`}>
      {variant !== 'default' && (
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotStyles[variant]}`} />
      )}
      <span className="font-mono font-bold text-sm leading-none">{value}</span>
      <span className="uppercase tracking-wider opacity-80">{label}</span>
    </div>
  );
}

export default function Header({ boxes, lastUpdated, isAdmin, onSettingsOpen, onSubscriptionsOpen, onUsageOpen, onLogout }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const online = boxes.filter((b) => b.status === 'online').length;
  const offline = boxes.filter((b) => b.status === 'offline').length;
  const printerErrors = boxes.filter(
    (b) =>
      b.status === 'online' &&
      b.printer_status === 'error' &&
      b.printer_error !== 'Drucker nicht angeschlossen',
  ).length;

  const timeStr = lastUpdated.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  function handleSettings() {
    setMobileMenuOpen(false);
    onSettingsOpen();
  }

  function handleSubscriptions() {
    setMobileMenuOpen(false);
    onSubscriptionsOpen();
  }

  function handleUsage() {
    setMobileMenuOpen(false);
    onUsageOpen();
  }

  function handleLogout() {
    setMobileMenuOpen(false);
    onLogout();
  }

  return (
    <header className="sticky top-0 z-20 bg-white border-b border-surface-border shadow-sm">
      {/* CI accent line */}
      <div className="h-0.5 bg-gradient-to-r from-brand-green via-brand-green to-transparent" />

      <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 sm:gap-6">
        {/* Logo + Title */}
        <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
          <img
            src="/fotobox-team-logo.png"
            alt="Fotobox Team"
            className="h-8 sm:h-10 w-auto object-contain flex-shrink-0"
          />
          <h1 className="font-raleway font-light text-sm sm:text-base uppercase tracking-[0.18em] text-ink-primary leading-tight">
            Status Monitoring
          </h1>
        </div>

        {/* Stats – nur auf Desktop sichtbar */}
        <div className="hidden sm:flex items-center gap-2 flex-1 justify-center">
          <StatPill value={boxes.length} label="Gesamt" variant="default" />
          <StatPill value={online} label="Online" variant="online" />
          <StatPill value={offline} label="Offline" variant="offline" />
          {printerErrors > 0 && (
            <StatPill value={printerErrors} label="Fehler" variant="warning" />
          )}
        </div>

        {/* Desktop: Zeit + Buttons */}
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          <div className="text-right hidden md:block">
            <p className="font-mono text-xs font-medium text-ink-primary">{timeStr}</p>
            <p className="text-ink-muted text-[10px] uppercase tracking-wider">Aktualisiert</p>
          </div>

          {isAdmin && (
            <button
              onClick={onSettingsOpen}
              className="w-8 h-8 rounded-lg border border-surface-border bg-surface-subtle flex items-center justify-center text-ink-muted hover:text-brand-green hover:border-brand-green transition-all duration-150"
              title="Einstellungen"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          )}

          <button
            onClick={onUsageOpen}
            className="w-8 h-8 rounded-lg border border-surface-border bg-surface-subtle flex items-center justify-center text-ink-muted hover:text-brand-green hover:border-brand-green transition-all duration-150"
            title="KI-Freistellungen – Verbrauch"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
          </button>

          <button
            onClick={onSubscriptionsOpen}
            className="w-8 h-8 rounded-lg border border-surface-border bg-surface-subtle flex items-center justify-center text-ink-muted hover:text-brand-green hover:border-brand-green transition-all duration-150"
            title="Benachrichtigungen einrichten"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
          </button>

          <button
            onClick={onLogout}
            className="w-8 h-8 rounded-lg border border-surface-border bg-surface-subtle flex items-center justify-center text-ink-muted hover:text-brand-red hover:border-brand-red transition-all duration-150"
            title="Abmelden"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
            </svg>
          </button>
        </div>

        {/* Mobile: Burger Button */}
        <button
          className="sm:hidden w-9 h-9 rounded-lg border border-surface-border bg-surface-subtle flex items-center justify-center text-ink-secondary flex-shrink-0"
          onClick={() => setMobileMenuOpen(v => !v)}
          aria-label="Menü"
        >
          {mobileMenuOpen ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Dropdown-Menü */}
      {mobileMenuOpen && (
        <div className="sm:hidden border-t border-surface-border bg-white px-4 py-3 space-y-1 shadow-md">
          {/* Mini-Stats */}
          <div className="flex gap-2 pb-3 border-b border-surface-border mb-1">
            <StatPill value={online} label="Online" variant="online" />
            <StatPill value={offline} label="Offline" variant="offline" />
            {printerErrors > 0 && <StatPill value={printerErrors} label="Fehler" variant="warning" />}
          </div>

          <p className="font-mono text-[10px] text-ink-faint pb-1">{timeStr} – Zuletzt aktualisiert</p>

          {isAdmin && <button
            onClick={handleSettings}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-opensans font-medium text-ink-secondary hover:bg-surface-subtle hover:text-brand-green transition-colors"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Einstellungen
          </button>}

          <button
            onClick={handleUsage}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-opensans font-medium text-ink-secondary hover:bg-surface-subtle hover:text-brand-green transition-colors"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
            KI-Verbrauch
          </button>

          <button
            onClick={handleSubscriptions}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-opensans font-medium text-ink-secondary hover:bg-surface-subtle hover:text-brand-green transition-colors"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            Benachrichtigungen
          </button>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-opensans font-medium text-brand-red hover:bg-brand-red-light transition-colors"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
            </svg>
            Abmelden
          </button>
        </div>
      )}
    </header>
  );
}
